<template>
  <v-app>
    <v-container fluid class="pa-0">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-calendar-check</v-icon>
                Prepare Attendance Configuration
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="handleSubmit">
                  <v-row class="py-3 px-3" align="end" dense>
                    <!-- Start Date -->
                    <v-col cols="12" md="2">
                      <v-date-input
                        v-model="formData.start_date"
                        label="Attendance Start Date *"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        hide-details="auto"
                        required
                      />
                    </v-col>

                    <!-- End Date -->
                    <v-col cols="12" md="2">
                      <v-date-input
                        v-model="formData.end_date"
                        label="Attendance End Date *"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        hide-details="auto"
                        required
                        :min="formData.start_date"
                      />
                    </v-col>

                    <!-- Year -->
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="formData.year"
                        :items="yearOptions"
                        label=" Salary Year *"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-inner-icon="mdi-calendar-year"
                        hide-details="auto"
                        required
                      />
                    </v-col>

                    <!-- Month -->
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="formData.month"
                        :items="months"
                        item-title="label"
                        item-value="value"
                        label=" Salary Month *"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-inner-icon="mdi-calendar-month"
                        hide-details="auto"
                        required
                      />
                    </v-col>

                    <!-- Submit Button -->
                    <v-col cols="12" md="3" class="d-flex align-end">
                      <v-btn
                        type="submit"
                        color="primary"
                        variant="flat"
                        :loading="loading"
                        prepend-icon="mdi-content-save"
                        density="compact"
                        height="40"
                      >
                        Prepare Attendance
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'

const router = useRouter()
const route = useRoute()
const store = useStore()
const loading = ref(false)
const panel = ref([0])
const editMode = ref(false)
const editId = ref(null)

// Form data
const formData = reactive({
  start_date: '',
  end_date: '',
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1
})

// Options
const months = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
]

// Generate year options (from 2020 to current year + 5)
const currentYear = new Date().getFullYear()
const yearOptions = computed(() => {
  const years = []
  for (let year = 2020; year <= currentYear + 5; year++) {
    years.push(year)
  }
  return years.reverse() // Show most recent years first
})

// Watch for date changes to auto-update year and month
watch([() => formData.start_date, () => formData.end_date], ([startDate, endDate]) => {
  if (startDate) {
    const date = new Date(startDate)
    formData.year = date.getFullYear()
    formData.month = date.getMonth() + 1
  }
})

// Methods
const setDefaultDates = () => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  
  formData.start_date = firstDay.toISOString().split('T')[0]
  formData.end_date = lastDay.toISOString().split('T')[0]
  formData.year = today.getFullYear()
  formData.month = today.getMonth() + 1
}

const validateForm = () => {
  if (!formData.start_date) {
    toast.error('Please select a start date')
    return false
  }

  if (!formData.end_date) {
    toast.error('Please select an end date')
    return false
  }

  if (new Date(formData.start_date) > new Date(formData.end_date)) {
    toast.error('Start date cannot be after end date')
    return false
  }

  if (!formData.year || formData.year < 2020 || formData.year > 2100) {
    toast.error('Please enter a valid year')
    return false
  }

  if (!formData.month || formData.month < 1 || formData.month > 12) {
    toast.error('Please select a valid month')
    return false
  }

  return true
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true

  try {
    const requestData = {
      salary_attendance_master_id: editMode.value ? editId.value : null,
      start_date: formData.start_date,
      end_date: formData.end_date,
      year: parseInt(formData.year),
      month: parseInt(formData.month)
    }

    const response = await store.dispatch('salaryReadyAttendance/saveUpdateSalaryReadyAttendance', requestData)

    if (response.status || response.success) {
      toast.success(response.message || 'Attendance prepared successfully!')
      // Reset form
      setDefaultDates()
      editMode.value = false
      editId.value = null
      // Redirect to summary page after successful preparation
      setTimeout(() => {
        router.push({ name: 'attendance-summary' })
      }, 1000) // Small delay to show success message
    } else {
      toast.error(response.message || 'Failed to prepare attendance')
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to prepare attendance'
    toast.error(errorMessage)
  } finally {
    loading.value = false
  }
}

// Load data for edit mode
const loadEditData = async () => {
  if (route.query.id) {
    editMode.value = true
    editId.value = parseInt(route.query.id)
    
    // Set form data from query parameters if available
    if (route.query.start_date) {
      formData.start_date = route.query.start_date
    }
    if (route.query.end_date) {
      formData.end_date = route.query.end_date
    }
    if (route.query.year) {
      formData.year = parseInt(route.query.year)
    }
    if (route.query.month) {
      formData.month = parseInt(route.query.month)
    }
  }
}

// Lifecycle
onMounted(() => {
  if (!route.query.id) {
    setDefaultDates()
  }
  loadEditData()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

